import React, { useState, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../AppContext';
import CommentModal from './CommentModal';
import BadgeIconStrip from './BadgeIconStrip';
import withVisionAura from './visions/withVisionAura';
import './Moments.css';

const ProfilePicture = withVisionAura(({ src, alt, onClick, className }) => (
    <img src={src} alt={alt} onClick={onClick} className={className} />
));

const ReelItem = ({ moment, isActive, onLike, onAmplify, onUserSelect, isMuted, onMuteToggle, onShareToVision, onReport }) => {
    const { LucideIcons, userId, appFunctions, setMessage, showConfirmation, userConnections, setUserConnections } = useAppContext();
    const { t } = useTranslation();
    const isOwner = moment.authorId === userId;
    const [showComments, setShowComments] = useState(false);
    const videoRef = useRef(null);
    const reelRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [showPlaybackIcon, setShowPlaybackIcon] = useState(false);
    const [isLiked, setIsLiked] = useState(false);
    const [isIntersecting, setIsIntersecting] = useState(false);
    const [isContentLoaded, setIsContentLoaded] = useState(false);

    const isInnerCircle = userConnections?.some(c => c.followingId === moment.authorId && c.tier === 'Inner Circle');

    const handleToggleInnerCircle = (e) => {
        e.stopPropagation();
        const funcName = isInnerCircle ? 'removeInnerCircleMember' : 'addInnerCircleMember';

        showConfirmation({
            message: isInnerCircle 
                ? (t('userProfile.actions.removeFromInnerCircleConfirmation') || "Remove from Inner Circle?")
                : (t('userProfile.actions.addToInnerCircleConfirmation') || "Add to Inner Circle?"),
            onConfirm: async () => {
                try {
                    const func = httpsCallable(appFunctions, funcName);
                    await func({ targetUserId: moment.authorId });
                    
                    // Optimistic update
                    if (typeof setUserConnections === 'function') {
                        setUserConnections(prev => {
                             const exists = prev.some(c => c.followingId === moment.authorId);
                             if (exists) {
                                 return prev.map(c => {
                                     if (c.followingId === moment.authorId) {
                                         return { ...c, tier: isInnerCircle ? 'Following' : 'Inner Circle' };
                                     }
                                     return c;
                                 });
                             } else if (!isInnerCircle) {
                                  return [...prev, { followingId: moment.authorId, tier: 'Inner Circle' }];
                             }
                             return prev;
                        });
                    }

                    setMessage(isInnerCircle 
                        ? (t('userProfile.success.removedFromInnerCircle') || "Removed from Inner Circle")
                        : (t('userProfile.success.addedToInnerCircle') || "Added to Inner Circle")
                    );
                } catch (error) {
                    setMessage(t('userProfile.errors.toggleInnerCircle', { message: error.message }) || `Error: ${error.message}`);
                }
            }
        });
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsIntersecting(entry.isIntersecting);
            },
            { rootMargin: "200px" } // Pre-load when it's 200px away from the viewport
        );
        if (reelRef.current) {
            observer.observe(reelRef.current);
        }
        return () => {
            if (reelRef.current) {
                observer.unobserve(reelRef.current);
            }
        };
    }, []);

    const hasLiked = moment.likes?.includes(userId);
    const isRevealed = moment.revealedTo && moment.revealedTo.includes(userId) && moment.echoesInvested >= 30;

    const handleVideoClick = () => {
        setIsPlaying(prev => !prev);
        setShowPlaybackIcon(true);
        setTimeout(() => setShowPlaybackIcon(false), 600);
    };

    const handleLikeClick = (e) => {
        e.stopPropagation();
        onLike(moment.id, moment.authorId);
        setIsLiked(true);
        setTimeout(() => setIsLiked(false), 300); // Reset after animation duration
    };
    
    useEffect(() => {
        setIsPlaying(isActive);
    }, [isActive]);

    useEffect(() => {
        if (videoRef.current) {
            if (isPlaying && isIntersecting) {
                videoRef.current.play().catch(error => {
                    console.error("Video autoplay was prevented:", error);
                    setIsPlaying(false);
                });
            } else {
                videoRef.current.pause();
            }
        }
    }, [isPlaying, isIntersecting]);

    const renderPlayer = () => {
        if (isOwner && ['pending', 'uploading', 'review_needed'].includes(moment.moderationStatus)) {
            return (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900 text-white text-center p-4">
                    <LucideIcons.ShieldCheck size={48} className="text-blue-400 mb-4 animate-pulse" />
                    <p className="font-bold text-lg">{t('momentsFeed.underReview')}</p>
                    <p className="text-sm text-gray-400">{t('momentsFeed.reviewMessage')}</p>
                </div>
            );
        }

        if (moment.moderationStatus === 'rejected') {
            return (
                <div className="w-full h-full flex flex-col items-center justify-center bg-red-900/50 text-white text-center p-4">
                    <LucideIcons.ShieldOff size={48} className="text-red-400 mb-4" />
                    <p className="font-bold text-lg">{t('momentsFeed.contentRemoved')}</p>
                    <p className="text-sm text-gray-300">{t('momentsFeed.violationMessage')}</p>
                </div>
            );
        }

        if (moment.moderationStatus === 'approved' && moment.mediaUrl) {
            const isLandscape = moment.videoWidth && moment.videoHeight && moment.videoWidth > moment.videoHeight;
            return (
                <video
                    ref={videoRef}
                    key={moment.id}
                    src={isIntersecting ? moment.mediaUrl : ''} // <-- LAZY LOADING LOGIC
                    loop
                    muted={isMuted}
                    playsInline
                    onLoadedData={() => setIsContentLoaded(true)}
                    className={`w-full h-full ${isLandscape ? 'object-contain' : 'object-cover'}`}
                ></video>
            );
        }

        return (
            <div className="w-full h-full flex flex-col items-center justify-center bg-black text-white text-center p-4">
                <LucideIcons.AlertTriangle size={48} className="text-yellow-500 mb-4" />
                <p className="font-bold">{t('momentsFeed.contentUnavailable')}</p>
            </div>
        );
    };

    return (
        <div ref={reelRef} className={`w-full h-full ${isActive ? 'active-reel-indicator' : ''} ${hasLiked ? 'liked-moment-indicator' : ''}`} onClick={handleVideoClick}>
            <div className="reel-player-wrapper">{renderPlayer()}</div>
            
            <div className={`reel-content-wrapper ${isContentLoaded ? 'visible' : ''}`}>
                <div className="reel-gradient-overlay"></div>

                {moment.moderationStatus === 'approved' && moment.mediaUrl && (
                    <>
                        <div className={`reel-playback-icon ${showPlaybackIcon ? 'visible' : ''}`}>
                            {isPlaying ? <LucideIcons.Pause size={64} /> : <LucideIcons.Play size={64} />}
                        </div>
                        <div className="reel-overlay"></div>
                        <div className="reel-ui-container">
                            <div className="reel-details">
                                {isRevealed ? (
                                    <div className="author-info" onClick={(e) => { e.stopPropagation(); onUserSelect(moment.authorId); }}>
                                        <ProfilePicture src={moment.author?.photoURL || "https://placehold.co/40x40/AEC6CF/FFFFFF?text=U"} alt={moment.author?.displayName} className="w-10 h-10 rounded-full object-cover border-2 border-white" onClick={(e) => { e.stopPropagation(); onUserSelect(moment.authorId); }} userId={moment.authorId} />
                                        <span>{moment.author?.displayName}<BadgeIconStrip badges={moment.author?.badges} /></span>
                                        {!isOwner && (
                                            <button onClick={handleToggleInnerCircle} className={`transition-colors p-1 rounded-full hover:bg-pink-500/10 ml-2 ${isInnerCircle ? 'text-red-500 hover:text-red-700' : 'text-gray-300 hover:text-pink-400'}`} title={isInnerCircle ? (t('userProfile.actions.removeFromInnerCircle') || "Remove from Inner Circle") : (t('userProfile.actions.addToInnerCircle') || "Add to Inner Circle")}>
                                                {isInnerCircle ? <LucideIcons.HeartOff size={16} /> : <LucideIcons.Heart size={16} />}
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="author-info">
                                        <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                                            <LucideIcons.Feather size={20} className="text-gray-400" />
                                        </div>
                                        <span>{t('momentsFeed.mysteriousMoment')}</span>
                                    </div>
                                )}
                                {moment.content && <p className="caption">{moment.content}</p>}
                            </div>
                            <div className="reel-actions">
                                <button onClick={(e) => { e.stopPropagation(); onMuteToggle(); }} className={`reel-action-button mute-button ${isMuted ? 'muted' : ''}`}>
                                    {isMuted ? <LucideIcons.VolumeX size={28} /> : <LucideIcons.Volume2 size={28} />}
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); onReport(moment); }} className="reel-action-button">
                                    <LucideIcons.Flag size={28} color="white" />
                                </button>
                                <button onClick={handleLikeClick} className={`reel-action-button ${isLiked ? 'like-animation' : ''}`}>
                                    <LucideIcons.Heart size={32} fill={moment.likes?.includes(userId) ? '#ef4444' : 'transparent'} color={moment.likes?.includes(userId) ? '#ef4444' : 'white'} />
                                    <span className="count">{moment.likesCount || 0}</span>
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); setShowComments(!showComments); }} className="reel-action-button">
                                    <LucideIcons.MessageCircle size={32} color="white" />
                                    <span className="count">{moment.commentsCount || 0}</span>
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); onAmplify(moment.id); }} className="reel-action-button">
                                    <LucideIcons.Flame size={32} color="#f59e0b" />
                                    <span className="count">{moment.echoesInvested || 0}</span>
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); onShareToVision(moment); }} className="reel-action-button">
                                    <LucideIcons.Send size={32} color="white" />
                                </button>
                            </div>
                        </div>
                        {showComments && (
                            <CommentModal 
                                post={moment} 
                                onClose={() => setShowComments(false)} 
                                contentType="moment" 
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ReelItem;